// Świat hero 47-A „parabola fanbase” + sekwencja „generatora fanowskich editów” (paczka H47, runda 47, sesja A).
//
// Port 1:1 `design/claude-design-wyniki/47-hero-fanbase-generator/A/src/47a-swiat.js` BEZ DOM:
// `swiat(o, 4701)` liczy nasadę (utwór), dwa ramiona paraboli, paczki editów tam, gdzie ramię przecina
// łuk-falę wokół utworu, i rząd nowych kont na każdym łuku; `pomiar()` to te same liczby kontraktu
// czytelności co w MAPIE §4; `edity()` to polska odmiana liczby editów, z której świat szacuje szerokość
// podpisu. Ziarno LCG (4701), stałe (CFG, ILE, AWATARY, STYLE) i kolejność losowań są przeniesione bez
// zmian; zmienia się tylko wyjście: `dane(swiat(o))` zamiast rysowania do DOM, bo świat NIE jest liczony
// w przeglądarce (`frontend/scripts/gen-landing-parabola.mjs` zapisuje `landingParabola.data.ts`, a test
// pilnuje, że plik danych = wynik `swiat()`).
//
// Ruch to zdarzenia jako dane (MAPA §2): `planFala` (salwa po montażu i pętla), `planBudowa` (powrót hero
// w widok) i `plan2` (sekwencja generatora) zwracają listy `{ sel, kf, t, d, e, f }`, gdzie `sel` to
// selektor względem korzenia grafiki; hooki (`useHeroMotion`, `useGeneratorSequence`) odpalają je
// przez Web Animations API. Animowane są WYŁĄCZNIE `transform` i `opacity`.
//
// Ten plik ładuje też Node 24 wprost (zdejmuje typy), więc nie ma tu enumów, przestrzeni nazw ani
// importów względnych. Nazwy po polsku jak w prototypie. Współrzędne w px pola grafiki.

export type Orient = 'v' | 'w' | 'h'
export type PlatformKey = 'tt' | 'ig' | 'yt'
type Pt = [number, number]

/** Próg orientacji `h` (nasada z lewej, ramiona w prawo) i `w` (pion 560 × 436); poniżej `v`. */
export const HERO_H_PX = 1336
export const HERO_W_PX = 600

/**
 * Konfiguracje: v = pion (telefon, nasada u góry, ramiona w dół), w = pion szeroki (768),
 * h = poziom (komputer, nasada z lewej, ramiona w prawo).
 * W, H: pole grafiki; V: wierzchołek paraboli (środek łuków); A, B: parabola b² = (B²/A)·a; pw, ph: pigułka utworu;
 * F: wysokość kadru paczki przy skali 1; D: średnica awatara przy skali 1; gap: odstęp awatarów na łuku; rz: odstęp łuków;
 * lod: łodyga paczka → pierwsze konto; r0 / Rmax: promień pierwszego / ostatniego łuku; skMin, skP: skala głębi;
 * fs / lab: pismo liczby editów przy skali 1 / najmniejsze dopuszczone; podp: ile najbliższych łuków niesie liczbę editów.
 */
export interface Cfg {
  W: number; H: number; V: Pt; osX: 0 | 1; A: number; B: number; pw: number; ph: number; F: number; D: number
  gap: number; rz: number; lod: number; podp: number; r0: number; Rmax: number; skMin: number; skP: number; fs: number; lab: number
}
export const CFG: Record<Orient, Cfg> = {
  v: { W: 390, H: 388, V: [195, 70], osX: 0, A: 300, B: 176, pw: 214, ph: 46, F: 34, D: 26, gap: 9, rz: 12, lod: 8, podp: 3,
    r0: 44, Rmax: 318, skMin: 0.46, skP: 0.9, fs: 12, lab: 10 },
  w: { W: 560, H: 436, V: [280, 74], osX: 0, A: 340, B: 250, pw: 250, ph: 48, F: 38, D: 30, gap: 10, rz: 13, lod: 10, podp: 3,
    r0: 50, Rmax: 360, skMin: 0.46, skP: 0.9, fs: 12, lab: 10 },
  h: { W: 880, H: 700, V: [290, 350], osX: 1, A: 510, B: 300, pw: 236, ph: 56, F: 46, D: 34, gap: 10, rz: 14, lod: 10, podp: 4,
    r0: 56, Rmax: 580, skMin: 0.42, skP: 0.9, fs: 13, lab: 10 },
}

// awatary kont: kadry scen (nostalgia + hyperpop); kadry paczek: 16 stylów editu
export const AWATARY = ['nostalgia-01-mokry-chodnik-t01', 'nostalgia-02-przystanek-we-mgle-t01', 'nostalgia-03-latarnia-i-deszcz-t01',
  'nostalgia-04-ulica-o-zmierzchu-t01', 'nostalgia-05-przejscie-wsrod-zieleni-t01', 'hyperpop-forest-t01', 'nostalgia-01-mokry-chodnik-t03',
  'nostalgia-02-przystanek-we-mgle-t03', 'nostalgia-03-latarnia-i-deszcz-t03', 'nostalgia-04-ulica-o-zmierzchu-t03',
  'nostalgia-05-przejscie-wsrod-zieleni-t03', 'hyperpop-forest-t04']
export const STYLE = ['aesthetic', 'bold_pink', 'brat', 'classic', 'elegant', 'glitch', 'hype', 'kinetic', 'minimal', 'sad', 'simple', 'statement',
  'y2k', 'y2k_aero', 'y2k_bling', 'y2k_pro']
const PLATF: PlatformKey[] = ['tt', 'ig', 'yt']
/** Liczby editów w paczkach (dane przykładowe, różne). */
export const ILE = [12, 9, 16, 24, 14, 30, 18, 40, 22, 27, 36, 48, 20, 32]
/** Fala utworu: 16 słupków, wysokość w dziesiątych częściach pola fali. */
export const FALA = [5, 8, 6, 9, 7, 4, 8, 9, 6, 5, 8, 7, 9, 5, 6, 4]
/** Fala pigułki generatora: 32 słupki z tych samych wysokości. */
export const FALA32 = FALA.concat(FALA.slice(7), FALA.slice(0, 7))
/** Czas utworu przykładowego (dana, nie wynik). */
export const TRACK_TIME = '2:48'

function lcg(seed: number): () => number {
  let s = seed >>> 0
  return () => { s = (Math.imul(s, 1664525) + 1013904223) >>> 0; return s / 4294967296 }
}
const r1 = (v: number) => Math.round(v * 10) / 10
const r3 = (v: number) => Math.round(v * 1000) / 1000
function odl(a: Pt, b: Pt): number { const dx = a[0] - b[0], dy = a[1] - b[1]; return Math.sqrt(dx * dx + dy * dy) }
/** Odległość punktu od odcinka. */
function odlOdc(p: Pt, a: Pt, b: Pt): number {
  const vx = b[0] - a[0], vy = b[1] - a[1], wx = p[0] - a[0], wy = p[1] - a[1]
  const L = vx * vx + vy * vy, t = L ? Math.max(0, Math.min(1, (wx * vx + wy * vy) / L)) : 0
  return odl(p, [a[0] + vx * t, a[1] + vy * t])
}
function przecina(a: Pt, b: Pt, c: Pt, d: Pt): boolean {
  const o = (p: Pt, q: Pt, r: Pt) => (q[0] - p[0]) * (r[1] - p[1]) - (q[1] - p[1]) * (r[0] - p[0])
  const d1 = o(c, d, a), d2 = o(c, d, b), d3 = o(a, b, c), d4 = o(a, b, d)
  return ((d1 > 0) !== (d2 > 0)) && ((d3 > 0) !== (d4 > 0))
}
function odlOdcOdc(a: Pt, b: Pt, c: Pt, d: Pt): number {
  if (przecina(a, b, c, d)) return 0
  return Math.min(odlOdc(a, c, d), odlOdc(b, c, d), odlOdc(c, a, b), odlOdc(d, a, b))
}
type Prost = { x0: number; y0: number; x1: number; y1: number }
/** Czy odcinek wchodzi w prostokąt z. */
function odlOdcProst(a: Pt, b: Pt, z: Prost): boolean {
  const P: Pt[] = [[z.x0, z.y0], [z.x1, z.y0], [z.x1, z.y1], [z.x0, z.y1]]
  const w = (p: Pt) => p[0] > z.x0 && p[0] < z.x1 && p[1] > z.y0 && p[1] < z.y1
  if (w(a) || w(b)) return true
  for (let i = 0; i < 4; i++) if (przecina(a, b, P[i], P[(i + 1) % 4])) return true
  return false
}

/** Polska odmiana: 2-4 edity (poza 12-14), reszta editów. Świat szacuje z niej szerokość podpisu. */
export function edity(n: number): string {
  const j = n % 10, d = n % 100
  return n + ' ' + (j >= 2 && j <= 4 && (d < 12 || d > 14) ? 'edity' : 'editów')
}

export interface Ramie { s: -1 | 1; p: Pt[]; L: number[] }
function ramie(c: Cfg, s: -1 | 1): Ramie {
  const pts: Pt[] = [], k = c.B * c.B / c.A
  for (let i = 0; i <= 240; i++) {
    const a = c.A * 1.18 * Math.pow(i / 240, 1.6), b = s * Math.sqrt(k * a)
    pts.push(c.osX ? [c.V[0] + a, c.V[1] + b] : [c.V[0] + b, c.V[1] + a])
  }
  const L = [0]
  for (let i = 1; i < pts.length; i++) L.push(L[i - 1] + odl(pts[i], pts[i - 1]))
  return { s, p: pts, L }
}
/** Punkt i styczna po długości łuku ramienia. */
export function naLuku(r: { p: Pt[]; L: number[] }, l: number): { c: Pt; t: Pt } {
  const L = r.L
  let i = 1
  while (i < L.length - 1 && L[i] < l) i++
  const t = (l - L[i - 1]) / ((L[i] - L[i - 1]) || 1), a = r.p[i - 1], b = r.p[i]
  const dx = b[0] - a[0], dy = b[1] - a[1], m = Math.sqrt(dx * dx + dy * dy) || 1
  return { c: [a[0] + dx * t, a[1] + dy * t], t: [dx / m, dy / m] }
}

/** Krawędź podpisu, która stoi w miejscu, gdy pole v / w kompensuje skalę pisma: ta od strony paczki (podpis rośnie od niej). */
export type Kotwica = 'l' | 'r' | 't' | 'b'
export interface Paczka {
  s: -1 | 1; si: number; i: number; a: number; rho: number; c: Pt; t: Pt; n: Pt; sk: number; fh: number; fw: number; R: number
  b0: number; ang: number; id: number; ile: number; kadry: string[]; fs: number; ost: boolean
  lab?: { x: number; y: number; w: number; h: number; ax: Kotwica; ay: Kotwica }; kont: number; d: number
}
export interface Konto { id: number; p: number; c: Pt; d: number; e: number; pf: PlatformKey; av: string; ost: boolean; j: number; th: number; rho: number; rz: number }
export interface Luk { p: number; luk: [number, number, number]; a: Pt; b: Pt; ost: boolean; rz: number }
type Zajete = Prost & { k?: undefined } | { k: Pt; r: number; pk: number }
export interface Swiat {
  o: Orient; c: Cfg; ramiona: Ramie[]; paczki: Paczka[]; konta: Konto[]; wachlarz: Luk[]; zajete: Zajete[]
  nasada: { c: Pt; w: number; h: number }; nRz: number; nKont: number; nPacz: number
}

/** Świat jednej orientacji (ziarno 4701): 1:1 `swiat()` z prototypu 47-A. */
export function swiat(o: Orient, seed = 4701): Swiat {
  const c = CFG[o], rnd = lcg(seed || 4701)
  const W = { o, c, ramiona: [], paczki: [], konta: [], wachlarz: [], zajete: [] } as unknown as Swiat
  let nKont = 0, nPacz = 0
  const k2 = c.B * c.B / c.A
  // nasada: pigułka utworu przed wierzchołkiem
  const pw = c.pw, ph = c.ph
  const pc: Pt = c.osX ? [c.V[0] - pw / 2 - 12, c.V[1]] : [c.V[0], c.V[1] - ph / 2 - 12]
  W.nasada = { c: pc, w: pw, h: ph }
  W.zajete.push({ x0: pc[0] - pw / 2 - 6, y0: pc[1] - ph / 2 - 6, x1: pc[0] + pw / 2 + 6, y1: pc[1] + ph / 2 + 6 })
  for (const s of [-1, 1] as const) W.ramiona.push(ramie(c, s))
  const pkt = (a: number, b: number): Pt => (c.osX ? [c.V[0] + a, c.V[1] + b] : [c.V[0] + b, c.V[1] + a])
  // FALE: łuki koncentryczne wokół wierzchołka (jak fala dźwięku od utworu). Łuk k ma promień rho_k; tam, gdzie przecina ramię,
  // stoi paczka; między dwiema paczkami łuk niesie rząd kont połączonych linią przerwaną na każdym awatarze.
  let rho = c.r0, i = 0
  const rzedy: { i: number; a: number; rho: number; sk: number }[] = []
  while (rho <= c.Rmax) {
    const sk = Math.max(c.skMin, 1 - (1 - c.skMin) * Math.pow((rho - c.r0) / (c.Rmax - c.r0), c.skP))
    const aa = (-k2 + Math.sqrt(k2 * k2 + 4 * rho * rho)) / 2
    rzedy.push({ i, a: aa, rho, sk })
    const Rp = Math.max(c.F * sk * 9 / 16 * 1.02, c.F * sk * 0.5)
    const skN = Math.max(c.skMin, 1 - (1 - c.skMin) * Math.pow((rho + c.D * sk + c.rz - c.r0) / (c.Rmax - c.r0), c.skP))
    rho += Math.max(c.D * sk + c.rz, Rp + Math.max(c.F * skN * 9 / 16 * 1.02, c.F * skN * 0.5) + 5); i++
  }
  W.nRz = rzedy.length
  for (const R_ of rzedy) {
    const b0 = Math.sqrt(k2 * R_.a), db = k2 / (2 * b0)
    for (const [si, s] of ([-1, 1] as const).entries()) {
      const P = pkt(R_.a, s * b0), tv: Pt = c.osX ? [1, s * db] : [s * db, 1], m = Math.sqrt(tv[0] * tv[0] + tv[1] * tv[1])
      const fh = c.F * R_.sk, fw = fh * 9 / 16, R = Math.max(fw * 1.02, fh * 0.5)
      // kierunek do wnętrza = styczna do łuku w stronę osi
      const ang = Math.atan2(P[1] - c.V[1], P[0] - c.V[0])
      let tn: Pt = [-Math.sin(ang), Math.cos(ang)]
      const doOsi: Pt = c.osX ? [0, c.V[1] - P[1]] : [c.V[0] - P[0], 0]
      if (tn[0] * doOsi[0] + tn[1] * doOsi[1] < 0) tn = [-tn[0], -tn[1]]
      W.paczki.push({ s, si, i: R_.i, a: R_.a, rho: R_.rho, c: P, t: [tv[0] / m, tv[1] / m], n: tn, sk: R_.sk, fh, fw, R, b0, ang } as Paczka)
    }
  }
  for (const p of W.paczki) {
    p.id = nPacz++
    p.ile = ILE[p.id % ILE.length]
    p.kadry = [STYLE[Math.floor(rnd() * 16)], STYLE[Math.floor(rnd() * 16)], STYLE[Math.floor(rnd() * 16)]]
    const fs = Math.round(c.fs * p.sk)
    p.fs = fs >= c.lab && p.i < c.podp ? fs : 0
    p.ost = p.i === W.nRz - 1
  }
  // podpisy liczby editów po stronie zewnętrznej (wolne pole poza parabolą), tylko gdy mieszczą się bez kolizji
  for (const p of W.paczki) W.zajete.push({ k: [p.c[0], p.c[1]], r: p.R + 2, pk: p.id })
  for (const p of W.paczki) {
    if (!p.fs) continue
    const tw = p.fs * 0.57 * edity(p.ile).length, th = p.fs * 1.3
    let x0: number, y0: number
    let out: Pt = [-p.t[1], p.t[0]]
    const doOsi: Pt = c.osX ? [0, c.V[1] - p.c[1]] : [c.V[0] - p.c[0], 0]
    if (out[0] * doOsi[0] + out[1] * doOsi[1] > 0) out = [-out[0], -out[1]]
    const P: Pt = [p.c[0] + out[0] * (p.R + 4), p.c[1] + out[1] * (p.R + 4)]
    if (out[0] < -0.35) x0 = P[0] - tw; else if (out[0] > 0.35) x0 = P[0]; else x0 = P[0] - tw / 2
    if (out[1] < -0.6) y0 = P[1] - th; else if (out[1] > 0.6) y0 = P[1]; else y0 = P[1] - th / 2
    const z = { x0: x0 - 2, y0: y0 - 2, x1: x0 + tw + 2, y1: y0 + th + 2 }
    if (z.x0 < 0 || z.x1 > c.W || z.y0 < 0 || z.y1 > c.H) { p.fs = 0; continue }
    let hit = false
    for (const q of W.zajete) {
      if (q.k && q.pk === p.id) continue
      if (q.k ? (q.k[0] + q.r > z.x0 && q.k[0] - q.r < z.x1 && q.k[1] + q.r > z.y0 && q.k[1] - q.r < z.y1)
        : (z.x1 > (q as Prost).x0 && z.x0 < (q as Prost).x1 && z.y1 > (q as Prost).y0 && z.y0 < (q as Prost).y1)) { hit = true; break }
    }
    // podpis nie może przeciąć ramienia
    if (!hit) for (const r of W.ramiona) { for (let j = 1; j < r.p.length; j++) if (odlOdcProst(r.p[j - 1], r.p[j], z)) { hit = true; break } if (hit) break }
    if (hit) { p.fs = 0; continue }
    // kotwica = krawędź od strony paczki: przy kompensacji skali pola v / w podpis rośnie od niej, w obu osiach
    // z dala od paczki (gdy podpis stoi na wysokości paczki, rośnie w pionie w stronę przeciwną do jej środka)
    p.lab = { x: x0, y: y0, w: tw, h: th,
      ax: out[0] < -0.35 ? 'r' : out[0] > 0.35 ? 'l' : (p.c[0] > x0 + tw / 2 ? 'r' : 'l'),
      ay: out[1] < -0.6 ? 'b' : out[1] > 0.6 ? 't' : (p.c[1] > y0 + th / 2 ? 'b' : 't') }
    W.zajete.push(z)
  }
  /** Konto nie wchodzi na paczkę, podpis, pigułkę ani ramię. */
  const kontoWolne = (cc: Pt, d: number) => {
    for (const q of W.paczki) if (odl(cc, q.c) < q.R + d / 2 + 4) return false
    for (const z of W.zajete) if (!z.k && cc[0] + d / 2 > z.x0 - 3 && cc[0] - d / 2 < z.x1 + 3 && cc[1] + d / 2 > z.y0 - 3 && cc[1] - d / 2 < z.y1 + 3) return false
    for (const r of W.ramiona) for (let j = 1; j < r.p.length; j++) if (odlOdc(cc, r.p[j - 1], r.p[j]) < d / 2 + 4) return false
    return true
  }
  // rząd kont na łuku: kąty od paczki lewej do prawej, co (d + odstęp) po łuku, zestaw wyśrodkowany na osi
  const osAng = c.osX ? 0 : Math.PI / 2
  for (let q = 0; q < W.paczki.length; q += 2) {
    const L = W.paczki[q], Rr = W.paczki[q + 1], rr = L.rho, d = c.D * L.sk, krok = (d + c.gap) / rr
    const zap = (L.R + c.lod * (0.5 + 0.5 * L.sk) + d / 2) / rr // kąt zajęty przez paczkę i łodygę
    const poL = Math.abs(L.ang - osAng), wolny = 2 * (poL - zap) // cały łuk między paczkami minus zapas
    const n = Math.max(0, Math.floor(wolny / krok) + 1)
    L.kont = Rr.kont = 0; L.d = Rr.d = d
    const ids: { th: number; d: number }[] = []
    for (let j = 0; j < n; j++) {
      const th = osAng + (j - (n - 1) / 2) * krok, cc: Pt = [c.V[0] + rr * Math.cos(th), c.V[1] + rr * Math.sin(th)]
      // przynależność: połowa bliżej lewego ramienia = paczka lewa
      let strona = c.osX ? (cc[1] < c.V[1] ? L : Rr) : (cc[0] < c.V[0] ? L : Rr)
      if (Math.abs(th - osAng) < 1e-6 && n % 2) strona = j % 2 ? Rr : L
      if (!kontoWolne(cc, d)) continue
      const idx = nKont++
      W.konta.push({ id: idx, p: strona.id, c: cc, d, e: idx % 3, pf: PLATF[Math.floor(rnd() * 3)], av: AWATARY[Math.floor(rnd() * AWATARY.length)], ost: strona.ost, j, th, rho: rr, rz: L.i })
      strona.kont++
      ids.push({ th, d })
    }
    // linia rzędu: łuk od paczki do paczki, przerwany na każdym awatarze (odstęp 2 px od krawędzi)
    const thL = L.ang, thR = Rr.ang
    const pts = [{ th: thL, r: L.R + 2 }].concat(ids.map((x) => ({ th: x.th, r: x.d / 2 + 2 }))).concat([{ th: thR, r: Rr.R + 2 }])
    pts.sort((x, y) => (thL < thR ? 1 : -1) * (x.th - y.th))
    for (let u = 1; u < pts.length; u++) {
      const t0 = pts[u - 1].th + Math.sign(pts[u].th - pts[u - 1].th) * pts[u - 1].r / rr
      const t1 = pts[u].th - Math.sign(pts[u].th - pts[u - 1].th) * pts[u].r / rr
      if (Math.abs(t1 - t0) * rr < 2 || Math.sign(t1 - t0) !== Math.sign(pts[u].th - pts[u - 1].th)) continue
      W.wachlarz.push({ p: (u <= pts.length / 2 ? L : Rr).id, luk: [rr, t0, t1], a: [c.V[0] + rr * Math.cos(t0), c.V[1] + rr * Math.sin(t0)], b: [c.V[0] + rr * Math.cos(t1), c.V[1] + rr * Math.sin(t1)], ost: L.ost, rz: L.i })
    }
  }
  W.nKont = nKont; W.nPacz = nPacz
  return W
}

export interface Pomiar {
  minOdstepPaczek: number; minPaczkaKonto: number; podpisow: number; kont: number; paczek: number; minOdstepAwatarow: number
  minOdstepLinii: number; minElement: number; minTekstPaczki: number; kontPrzyPaczkach: string
}
/** Pomiar kontraktu czytelności (liczby do MAPY §4). */
export function pomiar(W: Swiat): Pomiar {
  let minAA = 1e9, minLin = 1e9, minEl = 1e9, minTxt = 1e9
  W.konta.forEach((a, i) => {
    minEl = Math.min(minEl, a.d)
    W.konta.forEach((b, j) => { if (j > i) minAA = Math.min(minAA, odl(a.c, b.c) - a.d / 2 - b.d / 2) })
  })
  for (const p of W.paczki) { minEl = Math.min(minEl, p.fw); if (p.fs) minTxt = Math.min(minTxt, p.fs) }
  W.wachlarz.forEach((f, i) => {
    W.wachlarz.forEach((g, j) => { if (j > i && f.p !== g.p) minLin = Math.min(minLin, odlOdcOdc(f.a, f.b, g.a, g.b)) })
  })
  let minPP = 1e9, minPK = 1e9, nPod = 0
  W.paczki.forEach((p, i) => {
    if (p.lab) nPod++
    W.paczki.forEach((q, j) => { if (j > i) minPP = Math.min(minPP, odl(p.c, q.c) - p.R - q.R) })
    for (const k of W.konta) minPK = Math.min(minPK, odl(p.c, k.c) - p.R - k.d / 2)
  })
  return { minOdstepPaczek: r1(minPP), minPaczkaKonto: r1(minPK), podpisow: nPod, kont: W.nKont, paczek: W.nPacz, minOdstepAwatarow: r1(minAA),
    minOdstepLinii: r1(minLin), minElement: r1(minEl), minTekstPaczki: minTxt, kontPrzyPaczkach: W.paczki.map((p) => p.kont).join(',') }
}

/* ── dane do rysowania: to, co zapisuje generator `landingParabola.data.ts` (zaokrąglone do 0,1 px) ── */

export interface PaczkaDane {
  id: number; s: -1 | 1; i: number; x: number; y: number; fw: number; fh: number; sk: number
  /** Styczna ramienia (kopnięcie w przebiegu światła) i długość łuku ramienia do paczki (budowa). */
  t: Pt; l: number
  kadry: string[]; ile: number
  /**
   * Podpis liczby editów: lewy górny róg, pismo, krawędzie zakotwiczenia (x, y) i `kMin` = najmniejsza skala pola
   * v / w, przy której podpis powiększony do nominalnego pisma na ekranie nie wchodzi na nic (niżej się chowa;
   * null = mieści się do podłogi PODLOGA_K); null = paczka bez słów.
   */
  lab: { x: number; y: number; fs: number; ax: Kotwica; ay: Kotwica; kMin: number | null } | null
  ost: boolean
}
export interface KontoDane { id: number; p: number; x: number; y: number; d: number; e: 1 | 2 | 3; pf: PlatformKey; av: string; rz: number; ost: boolean }
export interface ParabolaData {
  o: Orient; W: number; H: number; osX: 0 | 1; V: Pt; nRz: number
  /** Gradient ramion w osi paraboli (x1 y1 x2 y2). */
  gx: [number, number, number, number]
  nasada: { x: number; y: number; w: number; h: number }
  /** Ramię: odcinki ścieżki części stałej (pocięte wokół paczek), ogon do łuku odnowy, polilinia do budowy. */
  ramiona: { s: -1 | 1; ram: string[]; ogon: string[]; p: Pt[]; L: number[] }[]
  /** Ścieżki linii łuków stałych (indeks = łuk) i łuku odnowy. */
  luki: string[][]
  lukOdnowy: string[]
  paczki: PaczkaDane[]
  konta: KontoDane[]
}

/** Polilinia ramienia pocięta wokół paczek (linia nie przechodzi przez kadr). */
function ciecie(P: Pt[], paczki: Paczka[]): Pt[][] {
  const seg: Pt[][] = []
  let cur: Pt[] = []
  for (const pt of P) {
    let w = false
    for (const q of paczki) if (odl(pt, q.c) < q.R + 3) { w = true; break }
    if (w) { if (cur.length > 1) seg.push(cur); cur = [] } else cur.push(pt)
  }
  if (cur.length > 1) seg.push(cur)
  return seg
}
const dPath = (pts: Pt[]) => 'M' + pts.map((p) => r1(p[0]) + ' ' + r1(p[1])).join('L')
function dLuk(f: Luk): string {
  const sw = f.luk[2] > f.luk[1] ? 1 : 0
  return 'M' + r1(f.a[0]) + ' ' + r1(f.a[1]) + 'A' + r1(f.luk[0]) + ' ' + r1(f.luk[0]) + ' 0 0 ' + sw + ' ' + r1(f.b[0]) + ' ' + r1(f.b[1])
}
/** Indeks punktu ramienia najbliższego paczce. */
function najblizszy(r: Ramie, pk: Paczka): number {
  let best = 0, bd = 1e9
  for (let i = 0; i < r.p.length; i++) { const dd = odl(r.p[i], pk.c); if (dd < bd) { bd = dd; best = i } }
  return best
}

/** Podłoga skali pola v / w dopasowanego do wysokości okna (przegląd Claude H47-CLAUDE-02). */
export const PODLOGA_K = 0.6

type Prostokat = { x0: number; y0: number; x1: number; y1: number }
/** Prostokąt podpisu powiększony `s` razy od jego kotwicy (to samo robi CSS: `scale(1 / k)` z `transform-origin`). */
export function podpisPowiekszony(lab: NonNullable<Paczka['lab']>, s: number): Prostokat {
  const w = lab.w * s, h = lab.h * s
  const x0 = lab.ax === 'l' ? lab.x : lab.x + lab.w - w
  const y0 = lab.ay === 't' ? lab.y : lab.y + lab.h - h
  return { x0, y0, x1: x0 + w, y1: y0 + h }
}
/** Czy podpis paczki `p` przy skali pola `k` (pismo powiększone 1 / k) wchodzi na pole, paczkę, konto, pigułkę, ramię albo inny widoczny podpis. */
function kolizjaPodpisu(W: Swiat, p: Paczka, k: number, widoczne: Paczka[]): boolean {
  const z = podpisPowiekszony(p.lab!, 1 / k)
  const odlP = (c: Pt) => Math.hypot(Math.max(z.x0 - c[0], 0, c[0] - z.x1), Math.max(z.y0 - c[1], 0, c[1] - z.y1))
  if (z.x0 < 0 || z.y0 < 0 || z.x1 > W.c.W || z.y1 > W.c.H) return true
  for (const q of W.paczki) if (odlP(q.c) <= q.R) return true
  for (const a of W.konta) if (odlP(a.c) <= a.d / 2) return true
  const n = W.nasada
  if (!(z.x1 < n.c[0] - n.w / 2 || z.x0 > n.c[0] + n.w / 2 || z.y1 < n.c[1] - n.h / 2 || z.y0 > n.c[1] + n.h / 2)) return true
  const w = (q: Pt) => q[0] > z.x0 && q[0] < z.x1 && q[1] > z.y0 && q[1] < z.y1
  for (const r of W.ramiona) for (const q of r.p) if (w(q)) return true
  for (const q of widoczne) if (q !== p) {
    const u = podpisPowiekszony(q.lab!, 1 / k)
    if (!(z.x1 <= u.x0 || z.x0 >= u.x1 || z.y1 <= u.y0 || z.y0 >= u.y1)) return true
  }
  return false
}
/**
 * Najmniejsza skala pola (co 0,01 od 1 w dół do PODLOGA_K), przy której podpis nie koliduje; podpisy są
 * sprawdzane od najbliższych łuków, a kolidujący chowa się pierwszy, więc przy braku miejsca znikają najdalsze.
 */
function kMin(W: Swiat, p: Paczka): number | null {
  const labs = W.paczki.filter((q) => q.lab).sort((a, b) => a.i - b.i || a.id - b.id)
  const progi = new Map<Paczka, number | null>()
  for (const q of labs) {
    let prog: number | null = null
    for (let s = 100; s >= PODLOGA_K * 100 - 1e-9; s--) {
      const k = s / 100
      const widoczne = labs.filter((x) => progi.has(x) && (progi.get(x) === null || (progi.get(x) as number) <= k))
      if (kolizjaPodpisu(W, q, k, widoczne)) { prog = Math.round((k + 0.01) * 100) / 100; break }
    }
    progi.set(q, prog)
  }
  return progi.get(p) ?? null
}

/** Świat → dane do rysowania i ruchu (to, co `rysuj()` prototypu pisało do DOM, jako liczby i ścieżki). */
export function dane(W: Swiat): ParabolaData {
  const c = W.c
  const stale = W.paczki.filter((p) => !p.ost), ost = W.paczki.filter((p) => p.ost)
  const ramiona = W.ramiona.map((r) => {
    const ostSt = stale.filter((p) => p.s === r.s).pop() as Paczka, fn = ost.filter((p) => p.s === r.s)[0]
    const k0 = najblizszy(r, ostSt), k1 = najblizszy(r, fn)
    return {
      s: r.s,
      ram: ciecie(r.p.slice(0, k0 + 1), stale).map(dPath),
      ogon: ciecie(r.p.slice(k0, Math.min(r.p.length, k1 + 5)), stale.concat(ost)).map(dPath),
      // budowa jedzie tylko do ostatniej stałej paczki: dalej polilinia nie jest potrzebna
      p: r.p.slice(0, k0 + 2).map((q) => [r1(q[0]), r1(q[1])] as Pt),
      L: r.L.slice(0, k0 + 2).map(r1),
    }
  })
  const luki: string[][] = []
  for (let k = 0; k < W.nRz - 1; k++) luki.push(W.wachlarz.filter((f) => f.rz === k).map(dLuk))
  const lukL = (p: Paczka) => { const r = W.ramiona.find((x) => x.s === p.s) as Ramie; return r1(r.L[najblizszy(r, p)]) }
  return {
    o: W.o, W: c.W, H: c.H, osX: c.osX, V: [c.V[0], c.V[1]], nRz: W.nRz,
    gx: c.osX ? [c.V[0], 0, c.V[0] + c.A, 0] : [0, c.V[1], 0, c.V[1] + c.A],
    nasada: { x: r1(W.nasada.c[0]), y: r1(W.nasada.c[1]), w: W.nasada.w, h: W.nasada.h },
    ramiona,
    luki,
    lukOdnowy: W.wachlarz.filter((f) => f.ost).map(dLuk),
    paczki: W.paczki.map((p) => ({
      id: p.id, s: p.s, i: p.i, x: r1(p.c[0]), y: r1(p.c[1]), fw: r1(p.fw), fh: r1(p.fh), sk: r3(p.sk),
      t: [r3(p.t[0]), r3(p.t[1])] as Pt, l: lukL(p), kadry: p.kadry, ile: p.ile,
      lab: p.lab ? { x: r1(p.lab.x), y: r1(p.lab.y), fs: p.fs, ax: p.lab.ax, ay: p.lab.ay, kMin: W.o === 'h' ? null : kMin(W, p) } : null, ost: p.ost,
    })),
    konta: W.konta.map((k) => ({ id: k.id, p: k.p, x: r1(k.c[0]), y: r1(k.c[1]), d: r1(k.d), e: (k.e + 1) as 1 | 2 | 3, pf: k.pf, av: k.av, rz: k.rz, ost: k.ost })),
  }
}

/** Rozmiar loga platformy na awatarze (reguła 53): od średnicy 18 px 10 px, od 30 px 12 px; mniejsze bez logo. */
export const logoPx = (d: number) => (d >= 30 ? 12 : d >= 18 ? 10 : 0)

/* ── ruch: zdarzenia jako dane (MAPA §2) ─────────────────────────────────────────────────────── */

export interface Zdarzenie {
  /** Selektor względem korzenia grafiki (pole części 1 albo sekcja części 2). */
  sel: string
  kf: Keyframe[]
  /** Start w ms od chwili odpalenia, czas trwania, łatwość, wypełnienie. */
  t: number
  d: number
  e: string
  f?: FillMode
}
export const EZ = { wyj: 'cubic-bezier(.16,.84,.3,1)', lin: 'linear', mk: 'cubic-bezier(.4,0,.2,1)' }
/** Okres pętli i długość budowy (ms). */
export const OKRES = 5600
export const BUDOWA = 2300

const pk = (id: number) => `.mp-hp-pk[data-p="${id}"]`
const ak = (id: number) => `.mp-hp-ak[data-k="${id}"]`

/** Utwór pulsuje: pierścień wokół pigułki + skok słupków fali. Pigułka i jej tekst stoją. */
function puls(ev: Zdarzenie[], t0: number, mocny: boolean): void {
  ev.push({ sel: '.mp-hp-nas .mp-hp-ut-p', kf: [{ opacity: 0.95, transform: 'scale(1)' }, { opacity: 0, transform: 'scale(1.16,1.9)' }], t: t0, d: 950, e: EZ.wyj })
  FALA.forEach((_, i) => {
    ev.push({ sel: `.mp-hp-nas .mp-hp-fala > i:nth-child(${i + 1})`,
      kf: [{ transform: 'scaleY(1)' }, { transform: 'scaleY(' + r1((mocny ? 1.5 : 1.3) + (i % 3) * 0.2) + ')' }, { transform: 'scaleY(.6)' }, { transform: 'scaleY(1)' }],
      t: t0 + i * 16, d: 620, e: EZ.mk })
  })
}
/** Konta łuku k w kolejności od paczek (końce) do osi. */
function kontaLuku(W: ParabolaData, k: number): KontoDane[] {
  const osP = W.osX ? 1 : 0, sr = W.V[osP]
  const kk = W.konta.filter((a) => a.rz === k && !a.ost)
  kk.sort((a, b) => Math.abs((osP ? b.y : b.x) - sr) - Math.abs((osP ? a.y : a.x) - sr))
  return kk
}
/** Nowa paczka dojeżdża na koniec każdego ramienia i zapala nowy łuk kont; łuk gaśnie przed kolejnym cyklem. */
function odnowa(ev: Zdarzenie[], W: ParabolaData, tR: number, tKon: number): void {
  const ost = W.paczki.filter((p) => p.ost)
  for (const fn of ost) {
    const prev = W.paczki.filter((p) => p.s === fn.s && !p.ost).pop() as PaczkaDane
    ev.push({ sel: `.mp-hp-fn ${pk(fn.id)} .mp-hp-pk-in`,
      kf: [{ transform: 'translate(' + r1(prev.x - fn.x) + 'px,' + r1(prev.y - fn.y) + 'px) scale(.45)' }, { transform: 'translate(0,0) scale(1.14)', offset: 0.75 }, { transform: 'scale(1)' }],
      t: tR, d: 620, e: EZ.wyj, f: 'backwards' })
  }
  ev.push({ sel: '.mp-hp-fn', kf: [{ opacity: 1 }, { opacity: 1, offset: 0.84 }, { opacity: 0 }], t: tR, d: tKon - tR, e: EZ.lin })
  ev.push({ sel: '.mp-hp-fn-l', kf: [{ opacity: 0 }, { opacity: 1 }], t: tR + 380, d: 420, e: EZ.lin, f: 'backwards' })
  const osP = W.osX ? 1 : 0, sr = W.V[osP]
  const fk = W.konta.filter((k) => k.ost)
  fk.sort((a, b) => Math.abs((osP ? b.y : b.x) - sr) - Math.abs((osP ? a.y : a.x) - sr))
  fk.forEach((k, m) => {
    const tt = tR + 420 + Math.floor(m / 2) * 58
    ev.push({ sel: `.mp-hp-fn ${ak(k.id)} .mp-hp-ak-in`, kf: [{ transform: 'scale(0)' }, { transform: 'scale(1.2)', offset: 0.7 }, { transform: 'scale(1)' }], t: tt, d: 420, e: EZ.wyj, f: 'backwards' })
    ev.push({ sel: `.mp-hp-fn ${ak(k.id)} .mp-hp-ak-r`, kf: [{ opacity: 0.85, transform: 'scale(1)' }, { opacity: 0, transform: 'scale(1.45)' }], t: tt + 120, d: 600, e: EZ.wyj })
  })
}

/**
 * Przebieg światła po paczkach (od nasady w głąb) + fala odnowy. `mocna` = salwa po montażu: kopnięcie 6 px
 * i fala zapaleń kont; inaczej spokojna pętla (3 px, bez kont). Nic poza grupą odnowy nie jest ukrywane.
 */
export function planFala(W: ParabolaData, t0: number, mocna: boolean): Zdarzenie[] {
  const ev: Zdarzenie[] = [], dk = 80
  puls(ev, t0, mocna)
  for (const p of W.paczki) {
    if (p.ost) continue
    const tp = t0 + 60 + p.i * dk
    ev.push({ sel: `${pk(p.id)} .mp-hp-pk-g`, kf: [{ opacity: 0 }, { opacity: 0.95, offset: 0.3 }, { opacity: 0 }], t: tp, d: 620, e: EZ.lin })
    const kx = r1(p.t[0] * (mocna ? 6 : 3)), ky = r1(p.t[1] * (mocna ? 6 : 3))
    ev.push({ sel: `${pk(p.id)} .mp-hp-pk-in`,
      kf: [{ transform: 'translate(0,0) scale(1)' }, { transform: 'translate(' + kx + 'px,' + ky + 'px) scale(' + (mocna ? 1.16 : 1.07) + ')', offset: 0.35 }, { transform: 'translate(0,0) scale(1)' }],
      t: tp, d: 480, e: EZ.mk })
  }
  if (mocna) for (let k = 0; k < W.nRz - 1; k++) {
    kontaLuku(W, k).forEach((a, m) => {
      const tt = t0 + 130 + k * dk + Math.floor(m / 2) * 30
      ev.push({ sel: `${ak(a.id)} .mp-hp-ak-in`, kf: [{ transform: 'scale(1)' }, { transform: 'scale(1.2)' }, { transform: 'scale(1)' }], t: tt, d: 380, e: EZ.mk })
      ev.push({ sel: `${ak(a.id)} .mp-hp-ak-r`, kf: [{ opacity: 0.85, transform: 'scale(1)' }, { opacity: 0, transform: 'scale(1.45)' }], t: tt, d: 600, e: EZ.wyj })
    })
  }
  const tR = t0 + 60 + (W.nRz - 1) * dk + 60
  odnowa(ev, W, tR, t0 + OKRES - 150)
  return ev
}

/**
 * Wejście w widok: paczki wystrzeliwują z nasady łańcuchem wzdłuż ramion, hamują na miejscach, łuki kont zapalają się
 * od utworu na zewnątrz. Stan wyjściowy (t = 0, wypełnienie `backwards`) jest ukryty, więc budowa gra WYŁĄCZNIE po
 * wyjściu hero z widoku i powrocie (stan wyjściowy ustawiony poza ekranem).
 */
export function planBudowa(W: ParabolaData, t0: number): Zdarzenie[] {
  const ev: Zdarzenie[] = [], D = 1180, K = 26
  puls(ev, t0, true)
  ev.push({ sel: '.mp-hp-ramiona', kf: [{ opacity: 0 }, { opacity: 1 }], t: t0 + 60, d: 420, e: EZ.lin, f: 'backwards' })
  for (const r of W.ramiona) {
    const st = W.paczki.filter((p) => p.s === r.s && !p.ost)
    const lk = st.map((p) => p.l), S = lk[lk.length - 1]
    const skAt = (l: number) => {
      if (l <= lk[0]) return st[0].sk
      for (let i = 1; i < lk.length; i++) if (l <= lk[i]) { const u = (l - lk[i - 1]) / (lk[i] - lk[i - 1]); return st[i - 1].sk + (st[i].sk - st[i - 1].sk) * u }
      return st[st.length - 1].sk
    }
    st.forEach((p, j) => {
      const kf: Keyframe[] = [], kfL: Keyframe[] = []
      for (let i = 0; i <= K; i++) {
        const u = i / K, e = 1 - Math.pow(1 - u, 3), l = lk[j] - S * (1 - e), q = naLuku(r, Math.max(0, l))
        const dx = r1(q.c[0] - p.x), dy = r1(q.c[1] - p.y), sc = l <= 1 ? 0 : r1(Math.min(1.6, skAt(l) / p.sk) * 100) / 100
        kf.push({ offset: u, transform: 'translate(' + dx + 'px,' + dy + 'px) scale(' + sc + ')' })
        kfL.push({ offset: u, transform: 'translate(' + dx + 'px,' + dy + 'px) scale(' + (l < lk[j] * 0.6 ? 0 : 1) + ')' })
      }
      ev.push({ sel: `${pk(p.id)} .mp-hp-pk-in`, kf, t: t0 + 120, d: D, e: EZ.lin, f: 'backwards' })
      if (p.lab) ev.push({ sel: `.mp-hp-pn[data-p="${p.id}"]`, kf: kfL, t: t0 + 120, d: D, e: EZ.lin, f: 'backwards' })
    })
  }
  const tL = t0 + 120 + D * 0.78
  for (let k = 0; k < W.nRz - 1; k++) {
    ev.push({ sel: `.mp-hp-rz[data-rz="${k}"]`, kf: [{ opacity: 0 }, { opacity: 1 }], t: tL + k * 70, d: 260, e: EZ.lin, f: 'backwards' })
    kontaLuku(W, k).forEach((a, m) => {
      const tt = tL + 40 + k * 70 + Math.floor(m / 2) * 26
      ev.push({ sel: `${ak(a.id)} .mp-hp-ak-in`, kf: [{ transform: 'scale(0)' }, { transform: 'scale(1.18)', offset: 0.7 }, { transform: 'scale(1)' }], t: tt, d: 420, e: EZ.wyj, f: 'backwards' })
      ev.push({ sel: `${ak(a.id)} .mp-hp-ak-r`, kf: [{ opacity: 0.85, transform: 'scale(1)' }, { opacity: 0, transform: 'scale(1.45)' }], t: tt + 100, d: 600, e: EZ.wyj })
    })
  }
  return ev
}

/* ── część 2: generator (pigułka → siatka unikatowych editów → konta) ── */

/** Sześć kont z nickami świata D: nick, platforma, awatar. */
export const NAZWY_KONT: [string, PlatformKey, string][] = [
  ['@nocna.jazda', 'tt', 'nostalgia-03-latarnia-i-deszcz-t01'], ['@zostan.chwile', 'ig', 'nostalgia-05-przejscie-wsrod-zieleni-t01'],
  ['Nocna Zmiana', 'yt', 'hyperpop-forest-t01'], ['@nocna.zmiana', 'tt', 'nostalgia-02-przystanek-we-mgle-t01'],
  ['@kadry.nocne', 'ig', 'nostalgia-04-ulica-o-zmierzchu-t03'], ['@mokry.chodnik', 'yt', 'nostalgia-01-mokry-chodnik-t01'],
]
/** Komórka siatki: `v` = ścieżka małego wideo bez rozszerzenia (`.mp4` + plakat `.jpg`), `k` = kadr z `kadry/`. */
export type KomorkaSiatki = { v: string } | { k: string }
/** 16 stylów + 3 prawdziwe edity (wideo) + sceny; wideo rozstawione, nie obok siebie. 24 komórki (h) albo 18. */
export function kadrySiatki(n: 18 | 24): KomorkaSiatki[] {
  const out: KomorkaSiatki[] = [], st = STYLE.slice()
  const sc = ['nostalgia-02-przystanek-we-mgle-t03', 'hyperpop-forest-t04', 'nostalgia-05-przejscie-wsrod-zieleni-t03',
    'nostalgia-03-latarnia-i-deszcz-t03', 'nostalgia-01-mokry-chodnik-t03']
  // trzy prawdziwe edity w małej wersji (`scripts/gen-hero47-media.py`)
  const [a, c, u] = ['/showcase/mini/aesthetic', '/showcase/mini/catchy', '/showcase/mini/ujecia']
  const wid: Record<number, string> = n === 24 ? { 2: a, 12: c, 21: u } : { 2: a, 9: c, 16: u }
  let si = 0, ci = 0
  for (let i = 0; i < n; i++) {
    if (wid[i]) out.push({ v: wid[i] })
    else if (si < st.length) out.push({ k: 'styl-' + st[(si++ * 7) % 16] })
    else out.push({ k: sc[ci++ % sc.length] })
  }
  return out
}

/** Warstwa ruchu części 2 zmierzona z układu (raz po montażu i przy zmianie rozmiaru). */
export interface WarstwaGen {
  /** Łączniki (tylko 1440): a = utwór → rzędy siatki, b = siatka → konta; położenie, długość, kąt w stopniach. */
  lacz: { g: 'a' | 'b'; x: number; y: number; w: number; a: number }[]
  /** Iskry: słupek fali → komórka siatki (przesunięcie od słupka). */
  iskry: { x: number; y: number; dx: number; dy: number }[]
  /** Lecące edity: komórka → konto (kadr komórki, rozmiar, przesunięcie). */
  loty: { x: number; y: number; w: number; h: number; dx: number; dy: number; bg: string }[]
}

/** Sekwencja generatora od 0 (MAPA §2, część 2). `kom` = liczba komórek siatki. */
export function plan2(o: Orient, war: WarstwaGen, kom: number): { ev: Zdarzenie[]; koniec: number } {
  const ev: Zdarzenie[] = [], T = 0
  for (let i = 0; i < 2; i++) ev.push({ sel: `.mp-hg-mk[data-i="${i}"] > span`, kf: [{ transform: 'translateY(108%)' }, { transform: 'translateY(0)' }], t: T + i * 110, d: 700, e: EZ.wyj, f: 'backwards' })
  // 02 i 03 dopiero po przelocie iskier i editów: na 390 lot przechodzi przez ich miejsce
  ;[250, 1800, 3500].forEach((tk, i) => ev.push({ sel: `.mp-hg-kr[data-i="${i}"] .mp-hg-kt > div`, kf: [{ transform: 'translateY(110%)' }, { transform: 'translateY(0)' }], t: T + tk, d: 560, e: EZ.wyj, f: 'backwards' }))
  ev.push({ sel: '.mp-hg-ut .mp-hp-ut', kf: [{ transform: 'translateX(-104%)' }, { transform: 'translateX(0)' }], t: T + 320, d: 640, e: EZ.wyj, f: 'backwards' })
  FALA32.forEach((_, i) => ev.push({ sel: `.mp-hg-ut .mp-hp-fala > i:nth-child(${i + 1})`, kf: [{ transform: 'scaleY(.15)' }, { transform: 'scaleY(1.5)', offset: 0.6 }, { transform: 'scaleY(1)' }], t: T + 560 + i * 22, d: 520, e: EZ.mk, f: 'backwards' }))
  war.lacz.filter((l) => l.g === 'a').forEach((l, i) => {
    ev.push({ sel: `.mp-hg-ln[data-g="a"][data-i="${i}"]`, kf: [{ transform: 'rotate(' + l.a + 'deg) scaleX(0)' }, { transform: 'rotate(' + l.a + 'deg) scaleX(1)' }], t: T + 1000 + i * 30, d: 380, e: EZ.wyj, f: 'backwards' })
  })
  const kol = o === 'h' ? 8 : 6
  const kolej: number[] = []
  for (let i = 0; i < kom; i++) kolej.push(i)
  // kaskada od strony wejścia: 1440 od lewej kolumny, 390 od górnego rzędu
  kolej.sort((a, b) => (o === 'h' ? (a % kol) - (b % kol) || a - b : a - b))
  kolej.forEach((ci, m) => {
    const tI = T + 1080 + m * 22, s = war.iskry[ci]
    if (s) {
      ev.push({ sel: `.mp-hg-isk[data-j="${ci}"]`,
        kf: [{ opacity: 1, transform: 'translate(0,0) scale(1)' }, { opacity: 1, transform: 'translate(' + r1(s.dx * 0.5) + 'px,' + r1(s.dy * 0.5 - 30) + 'px) rotate(90deg) scale(1.2)', offset: 0.5 }, { opacity: 1, transform: 'translate(' + s.dx + 'px,' + s.dy + 'px) rotate(180deg) scale(.6)' }],
        t: tI, d: 460, e: EZ.mk })
    }
    ev.push({ sel: `.mp-hg-ka[data-i="${ci}"]`, kf: [{ transform: 'scale(.2)', opacity: 0 }, { transform: 'scale(1.08)', opacity: 1, offset: 0.65 }, { transform: 'scale(1)', opacity: 1 }], t: tI + 420, d: 420, e: EZ.wyj, f: 'backwards' })
  })
  war.lacz.filter((l) => l.g === 'b').forEach((l, i) => {
    ev.push({ sel: `.mp-hg-ln[data-g="b"][data-i="${i}"]`, kf: [{ transform: 'rotate(' + l.a + 'deg) scaleX(0)' }, { transform: 'rotate(' + l.a + 'deg) scaleX(1)' }], t: T + 2480 + i * 60, d: 420, e: EZ.wyj, f: 'backwards' })
  })
  war.loty.forEach((f, j) => {
    const tL = T + 2500 + j * 80
    ev.push({ sel: `.mp-hg-lot[data-j="${j}"]`, kf: [{ opacity: 1, transform: 'translate(0,0) scale(1)' }, { opacity: 1, transform: 'translate(' + f.dx + 'px,' + f.dy + 'px) scale(.35)' }], t: tL, d: 560, e: EZ.mk })
    ev.push({ sel: `.mp-hg-kn[data-i="${j}"] .mp-hp-ak-in`, kf: [{ transform: 'scale(0)' }, { transform: 'scale(1.18)', offset: 0.65 }, { transform: 'scale(1)' }], t: tL + 470, d: 440, e: EZ.wyj, f: 'backwards' })
    ev.push({ sel: `.mp-hg-kn[data-i="${j}"] .mp-hp-ak-r`, kf: [{ opacity: 0.9, transform: 'scale(1)' }, { opacity: 0, transform: 'scale(1.45)' }], t: tL + 560, d: 640, e: EZ.wyj })
    ev.push({ sel: `.mp-hg-kn[data-i="${j}"] .mp-hg-nm b`, kf: [{ transform: 'translateX(-106%)' }, { transform: 'translateX(0)' }], t: tL + 540, d: 520, e: EZ.wyj, f: 'backwards' })
  })
  return { ev, koniec: T + 4100 }
}
